import { AccountTreeOutlined, CategoryOutlined, ChevronRightOutlined, SearchOutlined, SettingsOutlined } from "@mui/icons-material";
import { Box, Divider, InputAdornment, List, ListItemButton, ListItemIcon, ListItemText, TextField, Toolbar, Typography } from "@mui/material";
import { useMemo } from "react";
import { getGroupLabel } from "../helpers";
import { IWorkflowNodeConfig, WorkflowNodeGroup } from "../interfaces";

interface IEntityNavigatorProps {
  nodes: IWorkflowNodeConfig[];
  selectedKey: string;
  onSelect: (nodeKey: string) => void;
}

const drawerWidth = 320;

function getNodeIcon(group: WorkflowNodeGroup) {
  switch (group) {
    case WorkflowNodeGroup.Catalogs:
      return <CategoryOutlined fontSize="small" />;
    case WorkflowNodeGroup.Relationships:
      return <AccountTreeOutlined fontSize="small" />;
    default:
      return <SettingsOutlined fontSize="small" />;
  }
}

export const EntityTreeNavigator = (props: IEntityNavigatorProps) => {
  const { nodes, selectedKey, onSelect } = props;

  const groupedNodes = useMemo(() => {
    const result: Record<string, IWorkflowNodeConfig[]> = {};

    nodes.forEach((node) => {
      if (!result[node.group]) {
        result[node.group] = [];
      }
      result[node.group].push(node);
    });

    return result;
  }, [nodes]);

  return (
    <Box
      sx={{
        width: drawerWidth,
        borderRight: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Toolbar>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Workflow Studio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Catalogs, relationships and scoped rules
          </Typography>
        </Box>
      </Toolbar>

      <Divider />

      <Box sx={{ p: 2, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search sections"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          px: 1,
          pb: 2,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {Object.keys(groupedNodes).map((groupKey) => (
          <Box key={groupKey} sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ px: 2, color: "text.secondary", fontWeight: 700, letterSpacing: 0.7 }}>
              {getGroupLabel(groupKey as WorkflowNodeGroup)}
            </Typography>

            <List disablePadding>
              {groupedNodes[groupKey].map((node) => {
                const isSelected = node.key === selectedKey;

                return (
                  <ListItemButton
                    key={node.key}
                    selected={isSelected}
                    onClick={() => onSelect(node.key)}
                    sx={{
                      mx: 1,
                      mb: 0.5,
                      borderRadius: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>{getNodeIcon(node.group)}</ListItemIcon>

                    <ListItemText
                      primary={node.title}
                      secondary={node.subtitle}
                      primaryTypographyProps={{ fontWeight: isSelected ? 700 : 600 }}
                      secondaryTypographyProps={{ sx: { lineHeight: 1.2 } }}
                    />

                    <ChevronRightOutlined fontSize="small" color={isSelected ? "primary" : "disabled"} />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
